import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { Node } from '@xyflow/react';
import type { HandleAction, WidgetNodeData } from '../../widgets/types';
import { isOfficeClosedAllowed, isZeroRouteAllowed } from '../../widgets/handle-action-rules';

type HandleActionContextValue = {
  getHandleAction: (nodeId: string, handleId: string) => HandleAction | undefined;
  setHandleAction: (nodeId: string, handleId: string, action: HandleAction | undefined) => void;
  isZeroRouteAllowed: (nodeId: string, handleId: string) => boolean;
  isOfficeClosedAllowed: (nodeId: string, handleId: string) => boolean;
};

const HandleActionContext = createContext<HandleActionContextValue>({
  getHandleAction: () => undefined,
  setHandleAction: () => {},
  isZeroRouteAllowed: () => false,
  isOfficeClosedAllowed: () => false,
});

export function useHandleAction() {
  return useContext(HandleActionContext);
}

type HandleActionProviderProps = {
  nodes: Node<WidgetNodeData>[];
  onSetHandleAction: (nodeId: string, handleId: string, action: HandleAction | undefined) => void;
  children: ReactNode;
};

export function HandleActionProvider({ nodes, onSetHandleAction, children }: HandleActionProviderProps) {
  const startNode = useMemo(() => nodes.find((n) => n.type === 'start'), [nodes]);

  const value = useMemo<HandleActionContextValue>(
    () => ({
      getHandleAction: (nodeId, handleId) => {
        const node = nodes.find((n) => n.id === nodeId);
        return node?.data.handleActions?.[handleId];
      },
      setHandleAction: onSetHandleAction,
      isZeroRouteAllowed: (nodeId, handleId) => isZeroRouteAllowed(startNode, nodeId, handleId),
      isOfficeClosedAllowed: (nodeId, handleId) => isOfficeClosedAllowed(startNode, nodeId, handleId),
    }),
    [nodes, onSetHandleAction, startNode],
  );

  return <HandleActionContext.Provider value={value}>{children}</HandleActionContext.Provider>;
}
